import React, { useState, useMemo } from 'react';
import { Button } from '../components/ui/button';
import { Slider } from '../components/ui/slider';
import { ArrowRight, Info } from 'lucide-react';
import {
  calcMonthlyPayment, formatAzn, formatAmountInt,
  DEFAULT_INTEREST_RATE,
  AMOUNT_MIN, AMOUNT_MAX, AMOUNT_STEP,
  TERM_MIN, TERM_MAX, TERM_STEP,
  HERO_EXAMPLE_AMOUNT, HERO_EXAMPLE_TERM,
} from '../utils/loanCalc';
import { useApplicationModal } from '../context/ApplicationModalContext';
import { SEO } from '../components/SEO';

export const CalculatorPage = () => {
  const { openModal } = useApplicationModal();
  const [amount, setAmount] = useState(HERO_EXAMPLE_AMOUNT);
  const [term, setTerm] = useState(HERO_EXAMPLE_TERM);

  const monthly = useMemo(
    () => calcMonthlyPayment(amount, term, DEFAULT_INTEREST_RATE),
    [amount, term]
  );

  const totalPayable = useMemo(() => monthly * term, [monthly, term]);

  return (
    <div className="bg-slate-50/70">
      <SEO
        title="Kredit kalkulyatoru | İDK BOKT"
        description="Kredit məbləği və müddəti seçin, təxmini aylıq ödənişi hesablayın. Sadə və şəffaf kalkulyator ilə kredit planınızı qurun."
        path="/kalkulator"
      />
      {/* Intro */}
      <section>
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 pt-12 sm:pt-14 pb-10 sm:pb-12 text-center">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 leading-tight font-serif-display">
            Kredit kalkulyatoru
          </h1>
          <p className="mt-3 text-base sm:text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
            Məbləğ və müddəti seçərək təxmini aylıq ödənişinizi hesablayın.
          </p>
        </div>
      </section>

      {/* Calculator */}
      <section className="pb-12 sm:pb-16">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-[11fr_9fr] gap-6" data-testid="calc-container">
            {/* Left: inputs — subtle gradient border */}
            <div
              className="rounded-2xl p-[1px] bg-gradient-to-br from-blue-100 via-blue-50 to-white shadow-sm"
              data-testid="calc-inputs-wrapper"
            >
              <div
                className="bg-white rounded-2xl p-7 sm:p-8 flex flex-col h-full"
                data-testid="calc-inputs-panel"
              >
                <div className="mb-6">
                  <h2 className="text-lg font-semibold text-gray-900">Kredit parametrləri</h2>
                </div>

                <div className="flex-1 flex flex-col justify-center space-y-8">
                  {/* Amount */}
                  <div>
                    <div className="flex items-baseline justify-between mb-3">
                      <span className="text-sm font-medium text-gray-700">Kredit məbləği</span>
                      <span
                        className="text-2xl font-bold text-blue-700 tabular-nums"
                        data-testid="calc-amount-display"
                      >
                        {formatAmountInt(amount)}{' '}
                        <span className="text-base font-medium text-gray-500">₼</span>
                      </span>
                    </div>
                    <Slider
                      value={[amount]}
                      onValueChange={([v]) => setAmount(v)}
                      min={AMOUNT_MIN}
                      max={AMOUNT_MAX}
                      step={AMOUNT_STEP}
                      className="mt-2"
                      data-testid="calc-amount-slider"
                      aria-label="Kredit məbləği"
                    />
                    <div className="flex justify-between text-xs text-gray-500 mt-2 tabular-nums">
                      <span>{formatAmountInt(AMOUNT_MIN)} ₼</span>
                      <span>{formatAmountInt(AMOUNT_MAX)} ₼</span>
                    </div>
                  </div>

                  {/* Term */}
                  <div>
                    <div className="flex items-baseline justify-between mb-3">
                      <span className="text-sm font-medium text-gray-700">Kredit müddəti</span>
                      <span
                        className="text-2xl font-bold text-blue-700 tabular-nums"
                        data-testid="calc-term-display"
                      >
                        {term} <span className="text-base font-medium text-gray-500">ay</span>
                      </span>
                    </div>
                    <Slider
                      value={[term]}
                      onValueChange={([v]) => setTerm(v)}
                      min={TERM_MIN}
                      max={TERM_MAX}
                      step={TERM_STEP}
                      className="mt-2"
                      data-testid="calc-term-slider"
                      aria-label="Kredit müddəti"
                    />
                    <div className="flex justify-between text-xs text-gray-500 mt-2 tabular-nums">
                      <span>{TERM_MIN} ay</span>
                      <span>{TERM_MAX} ay</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: one unified result card */}
            <div
              className="bg-blue-700 text-white rounded-2xl shadow-sm p-7 sm:p-8 flex flex-col"
              data-testid="calc-result-panel"
            >
              <div className="flex-1 flex flex-col justify-center items-center text-center">
                <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-blue-100 mb-4">
                  Təxmini aylıq ödəniş
                </p>
                <p
                  className="font-bold tabular-nums leading-none tracking-tight"
                  data-testid="calc-monthly-value"
                >
                  <span className="text-4xl sm:text-5xl">{formatAzn(monthly)}</span>
                  <span className="ml-2 text-xl sm:text-2xl font-medium text-blue-100 align-baseline">₼</span>
                </p>

                <div className="mt-8 pt-6 border-t border-white/15 w-full text-center">
                  <p className="text-[11px] uppercase tracking-wider text-blue-100/80 mb-1">
                    Ümumi ödəniləcək məbləğ
                  </p>
                  <p
                    className="text-lg font-semibold tabular-nums"
                    data-testid="calc-total-payable"
                  >
                    {formatAzn(totalPayable)} <span className="text-sm font-medium text-blue-100">₼</span>
                  </p>
                </div>
              </div>

              <div className="mt-6">
                <Button
                  size="lg"
                  className="w-full bg-white text-blue-700 hover:bg-blue-50 h-12 text-base font-semibold shadow-none"
                  data-testid="calc-apply-btn"
                  onClick={() => openModal({ amount, term })}
                >
                  Bu şərtlərlə müraciət et
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </div>
            </div>
          </div>

          {/* Disclaimer strip — spans full width under both panels */}
          <div
            className="w-full mt-4 rounded-2xl border border-blue-100/70 bg-blue-50/60 px-5 py-4 flex items-start gap-3"
            data-testid="calc-disclaimer"
          >
            <Info className="h-4 w-4 text-blue-700 flex-shrink-0 mt-0.5" />
            <p className="text-xs sm:text-sm text-gray-700 leading-relaxed">
              <span className="font-semibold text-gray-900">Qeyd:</span> Hesablama məlumat xarakterlidir və rəsmi kredit təklifi deyil. Yekun şərtlər müraciətin
              qiymətləndirilməsindən sonra müəyyən edilir.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};
